// repo/primary-care-connect/frontend/src/pages/Portal/OhtAdmin/Overview/OverviewPage.tsx

import React, { useEffect, useMemo, useState } from "react";
import {
  getOhtProfile,
  getOhtClinics,
  getOhtDoctors,
  getOhtInsights,
  getOhtTargets,
  getOhtMessages,
  type OhtProfile,
  type OhtClinicsResult,
  type OhtDoctorsResult,
  type OhtInsightsResult,
  type OhtCityInsights,
  type OhtTargetsResult,
  type OhtTarget,
  type OhtMessagesResult,
  type OhtMessage,
} from "../../../../api/ohtAdmin";
import {
  downloadReportCsv,
  downloadReportPdf,
  type Report,
} from "../../../../utils/reportExport";
import { GlossaryIcon } from "../components/Glossary";
import { SkeletonKpiTile } from "../components/Skeleton";
import { ProgressToTarget } from "../components/ProgressToTarget";
import {
  resolveTimeRange,
  type TimeRangeValue,
} from "../components/TimeRangeFilter";
import "../components/Glossary.css";
import "../components/Skeleton.css";
import "../components/ProgressToTarget.css";
import OhtFsaCapacityMap from "./components/OhtFsaCapacityMap";
import "./OverviewPage.css";

type CalculatedOverviewTarget = OhtTarget & {
  expectedTodayOverride?: number;
  sourceFsaTargetCount?: number;
};

function rangeLabel(range: TimeRangeValue): string {
  if (range.key === "last30") return "Last 30 days";
  if (range.key === "last90") return "Last 90 days";
  if (range.key === "ytd") return "This year";
  if (range.key === "all") return "All time";
  if (range.key === "custom" && range.from && range.to) {
    return `${range.from} to ${range.to}`;
  }
  return "Custom range";
}

function rangeDaysForPace(range: TimeRangeValue): number | null {
  if (range.key === "last30") return 30;
  if (range.key === "last90") return 90;

  const msPerDay = 1000 * 60 * 60 * 24;

  if (range.key === "ytd") {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);

    return Math.max(1, Math.ceil((now.getTime() - start.getTime()) / msPerDay));
  }

  if (range.key === "custom" && range.from && range.to) {
    const start = new Date(`${range.from}T00:00:00`);
    const end = new Date(`${range.to}T23:59:59`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }

    return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / msPerDay));
  }

  return null;
}

function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function fmtDays(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(1)} days`;
}

function fmtRate(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(1)} / day`;
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-CA");
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function messageTypeLabel(
  type: OhtMessage["messageType"] | OhtMessage["type"]
): string {
  switch (type) {
    case "Announcement":
    case "announcement":
      return "Announcement";
    case "PolicyUpdate":
    case "policy_update":
      return "Policy update";
    case "CapacityRequest":
    case "capacity_request":
      return "Capacity request";
    case "OperationalNotice":
    case "operational_notice":
      return "Operational notice";
    default:
      return String(type || "Message");
  }
}

function targetBasisLabel(value: string | null | undefined): string {
  if (value === "FSA") return "Calculated from FSA targets";
  if (value === "OHT") return "Legacy whole-OHT target";
  return "No active target";
}

function linearExpectedToday(target: OhtTarget): number {
  const start = new Date(target.startDate).getTime();
  const end = target.endDate ? new Date(target.endDate).getTime() : NaN;
  const now = Date.now();

  if (!Number.isFinite(start)) return 0;

  if (!Number.isFinite(end) || end <= start) {
    return Math.min(target.targetPatients, target.completed || 0);
  }

  const elapsedPct = Math.max(0, Math.min(1, (now - start) / (end - start)));
  return Math.round(target.targetPatients * elapsedPct);
}

function isTargetCurrentlyActive(target: OhtTarget): boolean {
  const today = new Date();
  const start = target.startDate ? new Date(target.startDate) : null;
  const end = target.endDate ? new Date(target.endDate) : null;

  if (!start || Number.isNaN(start.getTime())) return false;
  if (start > today) return false;

  if (!end || Number.isNaN(end.getTime())) return true;
  return end >= today;
}

function isFsaTarget(target: OhtTarget): boolean {
  return target.targetLevel === "FSA" || target.scope === "FSA";
}

function earliestDate(values: Array<string | null | undefined>): string {
  const valid = values
    .filter((value): value is string => !!value)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  return valid[0]?.toISOString().slice(0, 10) || new Date().toISOString().slice(0, 10);
}

function latestDate(values: Array<string | null | undefined>): string {
  const valid = values
    .filter((value): value is string => !!value)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime());

  return valid[0]?.toISOString().slice(0, 10) || new Date().toISOString().slice(0, 10);
}

function sumTargetNumbers(
  targets: OhtTarget[],
  selector: (target: OhtTarget) => number | null | undefined
): number {
  return targets.reduce<number>((total, target) => {
    return total + (Number(selector(target)) || 0);
  }, 0);
}

function calculatedWholeOhtTargetFromFsas(
  targets: OhtTarget[]
): CalculatedOverviewTarget | null {
  const fsaTargets = targets.filter(isFsaTarget);
  const activeFsaTargets = fsaTargets.filter(isTargetCurrentlyActive);
  const sourceTargets = activeFsaTargets.length > 0 ? activeFsaTargets : fsaTargets;

  if (sourceTargets.length === 0) return null;

  const targetPatients = sumTargetNumbers(sourceTargets, (target) => target.targetPatients);
  const completed = sumTargetNumbers(sourceTargets, (target) => target.completed);
  const requests = sumTargetNumbers(sourceTargets, (target) => target.requests);
  const attachments30d = sumTargetNumbers(sourceTargets, (target) => target.attachments30d);
  const pacePerDay = sumTargetNumbers(sourceTargets, (target) => target.pacePerDay);
  const expectedToday = sumTargetNumbers(sourceTargets, linearExpectedToday);
  const gap = Math.max(0, targetPatients - completed);

  const percentComplete =
    targetPatients > 0 ? Math.min(100, Math.round((completed / targetPatients) * 100)) : 0;

  const estimatedDaysToTarget =
    gap <= 0 ? 0 : pacePerDay > 0 ? Math.ceil(gap / pacePerDay) : null;

  const status: OhtTarget["status"] =
    targetPatients <= 0 || gap <= 0
      ? "complete"
      : pacePerDay <= 0
      ? "stalled"
      : estimatedDaysToTarget !== null && estimatedDaysToTarget > 365
      ? "at-risk"
      : "on-track";

  return {
    ohtTargetId: 0,
    targetLevel: "FSA",
    scope: "FSA",
    name: "Calculated whole-OHT attachment target",
    fsa: null,
    fsaLabel: null,
    cityId: null,
    cityName: null,
    clinicId: null,
    clinicName: null,
    scopeLabel: "Calculated from FSA targets",
    targetPatients,
    startDate: earliestDate(sourceTargets.map((target) => target.startDate)),
    endDate: latestDate(sourceTargets.map((target) => target.endDate)),
    completed,
    requests,
    gap,
    percentComplete,
    pacePerDay,
    estimatedDaysToTarget,
    attachments30d,
    status,
    createdAt: "",
    createdByUserId: 0,
    createdByDisplayName: null,
    createdByEmail: null,
    expectedTodayOverride: expectedToday,
    sourceFsaTargetCount: sourceTargets.length,
  };
}

function activeOverviewTarget(targets: OhtTarget[]): CalculatedOverviewTarget | null {
  return calculatedWholeOhtTargetFromFsas(targets);
}

type KpiTone = "action" | "good" | "warn" | "danger" | "muted";

function KpiTile({
  label,
  value,
  hint,
  tone = "muted",
  glossary,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  tone?: KpiTone;
  glossary?: React.ReactNode;
}) {
  return (
    <div className={`ocf-card ocf-kpi ocf-kpi--${tone}`}>
      <div className="ocf-kpi-label">
        {label}
        {glossary ? <>{glossary}</> : null}
      </div>
      <div className="ocf-big-number">{value}</div>
      <div className="ocf-muted">{hint}</div>
    </div>
  );
}

function progressStatus(
  status: OhtTarget["status"]
): "on-track" | "at-risk" | "behind" | "complete" {
  if (status === "complete") return "complete";
  if (status === "at-risk") return "at-risk";
  if (status === "stalled") return "behind";
  return "on-track";
}

function MiniCityBarList({
  rows,
  valueKey,
  label,
}: {
  rows: OhtCityInsights[];
  valueKey: "remainingCapacity" | "applications" | "attachments" | "pipelineCount";
  label: string;
}) {
  const max = Math.max(1, ...rows.map((r) => Number(r[valueKey] || 0)));

  if (rows.length === 0) {
    return (
      <div className="oht-muted">
        No city-level data returned for this OHT.
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {rows.map((row) => {
        const value = Number(row[valueKey] || 0);
        const pct = Math.max(2, Math.round((value / max) * 100));

        return (
          <div key={`${row.cityId || row.cityName || "city"}-${valueKey}`}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 4,
                fontSize: 12,
                fontWeight: 700,
                color: "#334e68",
              }}
            >
              <span>{row.cityName || "Unknown city"}</span>
              <span>{fmtNumber(value)}</span>
            </div>
            <div
              aria-label={`${row.cityName || "Unknown city"}: ${fmtNumber(
                value
              )} ${label}`}
              style={{
                height: 10,
                borderRadius: 999,
                background: "#e7eef6",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  borderRadius: 999,
                  background: "#1669b3",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function OverviewPage() {
  const overviewRange = useMemo(() => resolveTimeRange("last30"), []);
  const fsaPaceWindowDays = useMemo(
    () => rangeDaysForPace(overviewRange),
    [overviewRange]
  );

  const [profile, setProfile] = useState<OhtProfile | null>(null);
  const [clinics, setClinics] = useState<OhtClinicsResult | null>(null);
  const [doctors, setDoctors] = useState<OhtDoctorsResult | null>(null);
  const [insights, setInsights] = useState<OhtInsightsResult | null>(null);
  const [targets, setTargets] = useState<OhtTargetsResult | null>(null);
  const [messages, setMessages] = useState<OhtMessagesResult | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullExportBusy, setFullExportBusy] = useState<boolean>(false);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [
          profileResp,
          clinicsResp,
          doctorsResp,
          insightsResp,
          targetsResp,
          messagesResp,
        ] = await Promise.all([
          getOhtProfile(controller.signal),
          getOhtClinics(controller.signal),
          getOhtDoctors({}, controller.signal),
          getOhtInsights(
            {
              from: overviewRange.from,
              to: overviewRange.to,
            },
            controller.signal
          ),
          getOhtTargets(controller.signal),
          getOhtMessages(controller.signal),
        ]);

        if (cancelled) return;

        setProfile(profileResp);
        setClinics(clinicsResp);
        setDoctors(doctorsResp);
        setInsights(insightsResp);
        setTargets(targetsResp);
        setMessages(messagesResp);
      } catch (err: any) {
        if (cancelled || err?.name === "AbortError") return;
        setError(String(err?.message || "Failed to load OHT overview."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [overviewRange]);

  const activeTarget = useMemo(
    () => activeOverviewTarget(targets?.items || []),
    [targets]
  );

  const targetProgress = useMemo(() => {
    if (!activeTarget) return null;

    return {
      expectedToday:
        activeTarget.expectedTodayOverride ?? linearExpectedToday(activeTarget),
      daysToTarget: activeTarget.estimatedDaysToTarget,
      pacePerDay: activeTarget.pacePerDay,
      status: progressStatus(activeTarget.status),
    };
  }, [activeTarget]);

  const fsasByGap = useMemo(() => {
    return [...(insights?.fsas || [])].sort(
      (a, b) => (b.gapVsTarget || 0) - (a.gapVsTarget || 0)
    );
  }, [insights]);

  const citiesByGap = useMemo(() => {
    return [...(insights?.cities || [])].sort(
      (a, b) => (b.gapVsTarget || 0) - (a.gapVsTarget || 0)
    );
  }, [insights]);

  const citiesByRemaining = useMemo(() => {
    return [...(insights?.cities || [])].sort(
      (a, b) => (b.remainingCapacity || 0) - (a.remainingCapacity || 0)
    );
  }, [insights]);

  const priorityClinics = useMemo(() => {
    return [...(clinics?.items || [])]
      .sort((a, b) => (b.pipelineCount || 0) - (a.pipelineCount || 0))
      .slice(0, 8);
  }, [clinics]);

  const isReady =
    !!profile && !!clinics && !!doctors && !!insights && !!targets && !!messages;

  function buildFullReport(): Report | null {
    if (!profile || !clinics || !doctors || !insights || !targets || !messages) {
      return null;
    }

    const subtitle = `${profile.name} (${profile.code}) · Overview snapshot · ${rangeLabel(
      overviewRange
    )} recent flow`;

    const chapters: Report["chapters"] = [
      {
        title: "Executive summary",
        summary:
          "OHT-scoped operational snapshot of clinics, accepting doctors, current capacity, active pipeline, recent attachment flow, geographic pressure, and calculated whole-OHT target progress from FSA targets.",
        pageBreakBefore: false,
        sections: [
          {
            type: "kpi",
            heading: "Headline metrics",
            items: [
              {
                label: "Clinics in OHT",
                value: fmtNumber(profile.clinicCount),
                hint: `${fmtNumber(
                  profile.doctorCount
                )} family doctors in FSA scope.`,
                tone: "blue",
              },
              {
                label: "Accepting doctors",
                value: `${fmtNumber(
                  profile.acceptingDoctorCount
                )} / ${fmtNumber(profile.doctorCount)}`,
                hint: "Accepting / total scoped family doctors.",
                tone: "green",
              },
              {
                label: "Remaining capacity",
                value: fmtNumber(insights.capacity.remainingTotal),
                hint: "Computed from declared capacity, used capacity, and remaining override logic.",
                tone: "amber",
              },
              {
                label: "Recent attachment pace",
                value: fmtRate(insights.throughput.pacePerDay),
                hint: `Rolling attachment pace from the live insights endpoint. Overview uses ${rangeLabel(
                  overviewRange
                )} for recent flow context.`,
                tone: "green",
              },
            ],
          },
          activeTarget
            ? {
                type: "kpi",
                heading: "Calculated target progress",
                items: [
                  {
                    label: "Target basis",
                    value: targetBasisLabel(
                      activeTarget.targetLevel || activeTarget.scope
                    ),
                    hint: `${fmtNumber(activeTarget.sourceFsaTargetCount)} FSA target(s) included.`,
                    tone: "blue",
                  },
                  {
                    label: "Target patients",
                    value: fmtNumber(activeTarget.targetPatients),
                    tone: "amber",
                  },
                  {
                    label: "Completed",
                    value: fmtNumber(activeTarget.completed),
                    tone: "green",
                  },
                  {
                    label: "Gap",
                    value: fmtNumber(activeTarget.gap),
                    tone: "amber",
                  },
                ],
              }
            : {
                type: "kpi",
                heading: "Target progress",
                items: [
                  {
                    label: "Target basis",
                    value: "No active FSA target",
                    tone: "grey",
                  },
                ],
              },
        ],
      },
      {
        title: "Capacity radar",
        summary:
          "Doctor-level current capacity totals and total active pipeline across the OHT.",
        pageBreakBefore: true,
        sections: [
          {
            type: "kpi",
            heading: "Capacity totals",
            items: [
              {
                label: "Declared capacity",
                value: fmtNumber(insights.capacity.declaredTotal),
                tone: "blue",
              },
              {
                label: "Used capacity",
                value: fmtNumber(insights.capacity.usedTotal),
                tone: "green",
              },
              {
                label: "Remaining capacity",
                value: fmtNumber(insights.capacity.remainingTotal),
                tone: "amber",
              },
              {
                label: "Total pipeline",
                value: fmtNumber(insights.pipeline.totalActive),
                tone: "grey",
              },
            ],
          },
          {
            type: "table",
            heading: "Clinics with highest active pipeline",
            columns: [
              "Clinic",
              "City",
              "FSA",
              "Doctors",
              "Accepting",
              "Remaining",
              "Pipeline",
              "Avg TTA",
            ],
            rows: priorityClinics.map((c) => [
              c.clinicName,
              c.cityName || "—",
              c.normalizedFsa || "—",
              c.doctorCount,
              c.acceptingDoctorCount,
              c.remainingCapacity,
              c.pipelineCount,
              c.avgDaysToAttachment != null
                ? `${c.avgDaysToAttachment.toFixed(1)} d`
                : "—",
            ]),
          },
        ],
      },
      {
        title: "Recent attachment flow",
        summary:
          "Applications, completed attachments, average time to attachment, and active pipeline for the recent operational window.",
        pageBreakBefore: true,
        sections: [
          {
            type: "kpi",
            heading: rangeLabel(overviewRange),
            items: [
              {
                label: "Applications in range",
                value: fmtNumber(insights.throughput.applicationsInRange),
                tone: "blue",
              },
              {
                label: "Attachments in range",
                value: fmtNumber(insights.throughput.attachmentsInRange),
                tone: "green",
              },
              {
                label: "Average time to attachment",
                value: fmtDays(insights.throughput.avgDaysToAttachment),
                tone: "amber",
              },
              {
                label: "Active pipeline",
                value: fmtNumber(insights.pipeline.totalActive),
                tone: "grey",
              },
            ],
          },
        ],
      },
      {
        title: "Geographic intelligence",
        summary:
          "FSA-level capacity, demand, pipeline, target gap, pace, and estimated time to target across the OHT's FSA coverage, with city shown as a derived rollup.",
        pageBreakBefore: true,
        sections: [
          {
            type: "table",
            heading: "FSA-level supporting data",
            columns: [
              "FSA",
              "Clinics",
              "Doctors",
              "Accepting",
              "Remaining",
              "Applications",
              "Attachments",
              "Pipeline",
              "Target",
              "Gap",
              "Avg TTA",
            ],
            rows: fsasByGap.map((f) => [
              f.fsaLabel || `FSA ${f.fsa}`,
              f.clinicCount,
              f.doctorCount,
              f.acceptingDoctorCount,
              f.remainingCapacity,
              f.applications,
              f.attachments,
              f.pipelineCount,
              f.targetPatients,
              f.gapVsTarget,
              f.avgDaysToAttachment != null
                ? `${f.avgDaysToAttachment.toFixed(1)} d`
                : "—",
            ]),
          },
          {
            type: "table",
            heading: "Derived city-level supporting data",
            columns: [
              "City",
              "Clinics",
              "Doctors",
              "Accepting",
              "Remaining",
              "Applications",
              "Attachments",
              "Pipeline",
              "Derived target",
              "Derived gap",
              "Avg TTA",
            ],
            rows: citiesByGap.map((c) => [
              c.cityName || "Unknown city",
              c.clinicCount,
              c.doctorCount,
              c.acceptingDoctorCount,
              c.remainingCapacity,
              c.applications,
              c.attachments,
              c.pipelineCount,
              c.targetPatients,
              c.gapVsTarget,
              c.avgDaysToAttachment != null
                ? `${c.avgDaysToAttachment.toFixed(1)} d`
                : "—",
            ]),
          },
        ],
      },
      {
        title: "Doctors",
        summary:
          "Doctor-level capacity and intake signals returned by the live OHT doctors endpoint.",
        pageBreakBefore: true,
        sections: [
          {
            type: "table",
            heading: "All doctors in OHT scope",
            columns: [
              "Doctor",
              "Clinic",
              "City",
              "Accepting",
              "Declared",
              "Used",
              "Remaining",
              "Pipeline",
              "Avg TTA",
              "Capacity updated",
            ],
            rows: doctors.items.map((d) => [
              d.fullName,
              d.clinicName,
              d.cityName || "—",
              d.acceptingNewPatients ? "Yes" : "No",
              d.declaredCapacity ?? "—",
              d.usedCapacity,
              d.remainingCapacity ?? "—",
              d.pipelineCount,
              d.avgDaysToAttachment != null
                ? `${d.avgDaysToAttachment.toFixed(1)} d`
                : "—",
              fmtDate(d.remainingCapacityUpdatedAt),
            ]),
          },
        ],
      },
      {
        title: "Messages",
        summary: messages.items.length
          ? "OHT messages sent to clinics, including acknowledgement progress where required."
          : "No OHT messages have been sent yet.",
        pageBreakBefore: true,
        sections: [
          {
            type: "table",
            heading: "Sent messages",
            columns: [
              "Sent",
              "Type",
              "Title",
              "Scope",
              "Recipients",
              "Acknowledged",
              "Responses",
            ],
            rows: messages.items.length
              ? messages.items.map((m) => [
                  fmtDate(m.sentAt || m.createdAt),
                  messageTypeLabel(m.messageType),
                  m.title,
                  m.targetScope,
                  m.recipientCount,
                  m.acknowledgedCount,
                  m.responseCount,
                ])
              : [["—", "No messages", "", "", "", "", ""]],
          },
        ],
      },
    ];

    return {
      pageLabel: "OHT Admin Portal — Overview",
      subtitle,
      chapters,
    };
  }

  async function onExportCsv() {
    setFullExportBusy(true);
    try {
      const report = buildFullReport();
      if (report) downloadReportCsv(report, `oht-overview-${overviewRange.key}`);
    } finally {
      setFullExportBusy(false);
    }
  }

  async function onExportPdf() {
    setFullExportBusy(true);
    try {
      const report = buildFullReport();
      if (report) downloadReportPdf(report, `oht-overview-${overviewRange.key}`);
    } finally {
      setFullExportBusy(false);
    }
  }

  return (
    <div className="oht-page">
      <header className="oht-page-header">
        <div className="oht-page-title-row">
          <div>
            <h1 className="oht-page-title">Overview</h1>
            <p className="oht-page-subtitle">
              Current OHT capacity, target progress, FSA pressure, recent
              attachment flow, and operational signals.
            </p>
          </div>
          <div className="oht-page-actions">
            <button
              className="ocf-btn ocf-btn--secondary"
              type="button"
              onClick={onExportCsv}
              disabled={!isReady || fullExportBusy}
              title="Download CSV with the OHT overview snapshot"
            >
              {fullExportBusy ? "Building…" : "Export CSV"}
            </button>
            <button
              className="ocf-btn ocf-btn--primary"
              type="button"
              onClick={onExportPdf}
              disabled={!isReady || fullExportBusy}
              title="Download PDF with the OHT overview snapshot"
            >
              {fullExportBusy ? "Building…" : "Export PDF"}
            </button>
          </div>
        </div>
        <p className="oht-export-help">
          Overview shows the current operational picture. Recent flow and pace
          use {rangeLabel(overviewRange)}; custom date analysis belongs in
          Reports.
        </p>
      </header>

      <section className="ocf-card oht-filters">
        <div className="oht-filters-head">
          <h2 className="ocf-section-title">Operational snapshot</h2>
          {profile ? (
            <span className="oht-section-hint">
              {profile.name} · {profile.code} · {profile.fsas?.length ?? 0} FSAs
            </span>
          ) : null}
        </div>

        <p className="oht-muted" style={{ margin: 0, lineHeight: 1.5 }}>
          This page is intentionally not time-filtered. Capacity, targets,
          pipeline, and geography are shown as a current planning snapshot.
          Recent applications, attachments, pace, and FSA ETA use{" "}
          {rangeLabel(overviewRange)} as the operational pace window.
        </p>
      </section>

      {activeTarget && targetProgress ? (
        <ProgressToTarget
          targetName="Calculated whole-OHT attachment target"
          timeRangeLabel={`Current target · ${targetBasisLabel(
            activeTarget.targetLevel || activeTarget.scope
          )} · ${fmtNumber(activeTarget.sourceFsaTargetCount)} FSA target${
            activeTarget.sourceFsaTargetCount === 1 ? "" : "s"
          }`}
          goal={activeTarget.targetPatients}
          attached={activeTarget.completed}
          expectedToday={targetProgress.expectedToday}
          daysToTarget={targetProgress.daysToTarget}
          pacePerDay={targetProgress.pacePerDay}
          remainingCapacity={insights?.capacity.remainingTotal ?? null}
          status={targetProgress.status}
        />
      ) : null}

      {error ? <div className="oht-page-error">{error}</div> : null}

      <div className="oht-overview-snapshot">
        <section className="ocf-card oht-section" style={{ gridColumn: "1 / -1" }}>
          <div className="oht-section-head">
            <h2 className="ocf-section-title">Geographic intelligence</h2>
            <span className="oht-section-hint">
              FSA-level planning with derived city highlights
            </span>
          </div>

          <OhtFsaCapacityMap
            fsaRows={insights?.fsas || []}
            ohtFsas={profile?.fsas || []}
            isLoading={loading}
            paceWindowDays={fsaPaceWindowDays}
            paceWindowLabel={rangeLabel(overviewRange)}
          />

          {!loading ? (
            <div style={{ marginTop: 22 }}>
              <div className="oht-section-head" style={{ marginBottom: 8 }}>
                <h3 className="ocf-section-title" style={{ fontSize: 16 }}>
                  City highlights
                </h3>
                <span className="oht-section-hint">
                  City rows are derived analytics only; targets are not created
                  at city level
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: 16,
                }}
              >
                <div className="ocf-card">
                  <div className="ocf-kpi-label" style={{ marginBottom: 10 }}>
                    Remaining capacity by city
                  </div>
                  <MiniCityBarList
                    rows={citiesByRemaining.slice(0, 8)}
                    valueKey="remainingCapacity"
                    label="remaining capacity"
                  />
                </div>
                <div className="ocf-card">
                  <div className="ocf-kpi-label" style={{ marginBottom: 10 }}>
                    Active pipeline by city
                  </div>
                  <MiniCityBarList
                    rows={citiesByGap.slice(0, 8)}
                    valueKey="pipelineCount"
                    label="active pipeline"
                  />
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <section className="ocf-card oht-section">
          <div className="oht-section-head">
            <h2 className="ocf-section-title">OHT snapshot</h2>
            <span className="oht-section-hint">Current scope</span>
          </div>

          <div className="oht-kpi-grid">
            {loading ? (
              <>
                <SkeletonKpiTile />
                <SkeletonKpiTile />
                <SkeletonKpiTile />
              </>
            ) : (
              <>
                <KpiTile
                  label="Clinics in scope"
                  value={fmtNumber(profile?.clinicCount)}
                  hint="Clinics matched to this OHT by FSA coverage."
                  tone="action"
                  glossary={<GlossaryIcon term="oht" />}
                />
                <KpiTile
                  label="Accepting doctors"
                  value={`${fmtNumber(profile?.acceptingDoctorCount)} / ${fmtNumber(
                    profile?.doctorCount
                  )}`}
                  hint="Accepting / total scoped family doctors."
                  tone="good"
                />
                <KpiTile
                  label="Active admins"
                  value={fmtNumber(profile?.activeAdminCount)}
                  hint="OHT Admin users currently assigned to this OHT."
                  tone="muted"
                />
              </>
            )}
          </div>
        </section>

        <section className="ocf-card oht-section">
          <div className="oht-section-head">
            <h2 className="ocf-section-title">Capacity radar</h2>
            <span className="oht-section-hint">
              Current doctor-level capacity and active patient pipeline
            </span>
          </div>

          <div className="oht-kpi-grid">
            {loading ? (
              <>
                <SkeletonKpiTile />
                <SkeletonKpiTile />
                <SkeletonKpiTile />
                <SkeletonKpiTile />
              </>
            ) : (
              <>
                <KpiTile
                  label="Declared capacity"
                  value={fmtNumber(insights?.capacity.declaredTotal)}
                  hint="Total patient spaces declared by scoped family doctors."
                  tone="action"
                />
                <KpiTile
                  label="Used capacity"
                  value={fmtNumber(insights?.capacity.usedTotal)}
                  hint="Completed attachments counted against declared capacity."
                  tone="good"
                  glossary={<GlossaryIcon term="usedCapacity" />}
                />
                <KpiTile
                  label="Remaining capacity"
                  value={fmtNumber(insights?.capacity.remainingTotal)}
                  hint="Declared capacity minus used capacity, respecting remaining-capacity overrides."
                  tone="warn"
                />
                <KpiTile
                  label="Total pipeline"
                  value={fmtNumber(insights?.pipeline.totalActive)}
                  hint="Submitted, in review, waitlisted, or accepted pending attachment."
                  tone={insights?.pipeline.totalActive ? "action" : "good"}
                />
              </>
            )}
          </div>

          {!loading && insights ? (
            <div className="oht-muted">
              Latest capacity update:{" "}
              {fmtDateTime(insights.capacity.latestCapacityUpdatedAt)} · Stale
              capacity signals: {fmtNumber(insights.capacity.staleCapacityCount)}
            </div>
          ) : null}
        </section>

        <section className="ocf-card oht-section">
          <div className="oht-section-head">
            <h2 className="ocf-section-title">Recent attachment flow</h2>
            <span className="oht-section-hint">{rangeLabel(overviewRange)}</span>
          </div>

          <div className="oht-kpi-grid">
            {loading ? (
              <>
                <SkeletonKpiTile />
                <SkeletonKpiTile />
                <SkeletonKpiTile />
                <SkeletonKpiTile />
              </>
            ) : (
              <>
                <KpiTile
                  label="Applications"
                  value={fmtNumber(insights?.throughput.applicationsInRange)}
                  hint={`Patient requests submitted in the ${rangeLabel(
                    overviewRange
                  ).toLowerCase()} operational window.`}
                  tone="action"
                  glossary={<GlossaryIcon term="application" />}
                />
                <KpiTile
                  label="Attachments"
                  value={fmtNumber(insights?.throughput.attachmentsInRange)}
                  hint={`Attachments completed in the ${rangeLabel(
                    overviewRange
                  ).toLowerCase()} operational window.`}
                  tone="good"
                  glossary={<GlossaryIcon term="attachment" />}
                />
                <KpiTile
                  label="Attachment pace"
                  value={fmtRate(insights?.throughput.pacePerDay)}
                  hint="Recent pace based on live attachment activity."
                  tone="good"
                  glossary={<GlossaryIcon term="pace" />}
                />
                <KpiTile
                  label="Average TTA"
                  value={fmtDays(insights?.throughput.avgDaysToAttachment)}
                  hint="Average days from request submission to attachment completion."
                  tone="warn"
                  glossary={<GlossaryIcon term="tta" />}
                />
              </>
            )}
          </div>
        </section>
      </div>

      <details className="ocf-card oht-section oht-about-section">
        <summary
          style={{
            cursor: "pointer",
            listStyle: "none",
          }}
        >
          <div className="oht-section-head">
            <h2 className="ocf-section-title">About this page</h2>
            <span className="oht-section-hint">
              Click to expand or collapse the OHT Admin guide
            </span>
          </div>
        </summary>

        <div
          className="oht-about-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 16,
            marginTop: 16,
          }}
        >
          <div>
            <h3>What this page shows</h3>
            <p>
              This page gives OHT Admin users a current operational view of
              primary care attachment activity across the OHT. It summarizes
              clinic participation, accepting doctors, remaining capacity,
              active patient pipeline, geographic pressure, FSA pace and ETA,
              recent attachment flow, and progress against the calculated
              whole-OHT attachment target.
            </p>
          </div>

          <div>
            <h3>Who sees it</h3>
            <p>
              This page is available to OHT Admin users only. It is designed for
              regional planning, capacity awareness, and operational support.
              It should be used to understand system pressure and guide
              coordination, not to rank clinics or physicians.
            </p>
          </div>

          <div>
            <h3>How it updates</h3>
            <p>
              The overview refreshes when loaded and shows the current planning
              picture. The whole-OHT target is calculated from FSA targets.
              Capacity, pipeline, and geography are current-state signals.
              Recent flow, pace, and ETA use a fixed recent window so users are
              not misled by a global timeline filter.
            </p>
          </div>
        </div>
      </details>
    </div>
  );
}